import * as React from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { BG, AMBER } from "../../Global/colors";
import { FONT_FAMILY, BAD_RED } from "./designTokens";
import { FA_DIALOG_PAPER_SX } from "./FALayout";

// neura.css .neura-checkbox__box — an amber-FILLED box with a dark tick, not MUI's
// outlined glyph.
// Two sizes: 22px/r6 standard, 18px/r5 compact. Rendered as a real <input> inside a
// <label> so the whole row is a hit target and the control stays keyboard-reachable,
// which MUI's Checkbox gave us but the design's own markup also relies on.
export function FACheckbox({ checked, onChange, disabled, compact, label, labelSx, sx }) {
  const size = compact ? 18 : 22;
  const radius = compact ? 5 : 6;
  const tick = compact ? 11 : 14;

  return (
    <Box
      component="label"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        cursor: disabled ? "default" : "pointer",
        minWidth: 0,
        // The outer element is what sits in the caller's row, so anything that has to size
        // against that row belongs here. labelSx reaches only the text, which cannot fill a
        // parent that is itself shrink-to-fit.
        ...sx,
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={disabled ? undefined : onChange}
        sx={{ position: "absolute", opacity: 0, width: "1px", height: "1px" }}
      />
      <Box
        aria-hidden
        sx={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: `${radius}px`,
          flexShrink: 0,
          border: `1.5px solid ${checked ? AMBER : "rgba(255,255,255,0.25)"}`,
          backgroundColor: checked ? AMBER : "transparent",
          color: checked ? BG : "transparent",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.16s ease-out",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <svg width={tick} height={tick} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </Box>
      {label ? (
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "rgba(255,255,255,0.88)", minWidth: 0, ...labelSx }}>
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

// Replaces window.confirm, which renders OS chrome in a dark themed app and can't carry
// the design's per-screen copy (its cancel labels are things like "Keep checking").
// FA_DIALOG_PAPER_SX targets "& .MuiDialog-paper", so it belongs on sx — routing it
// through PaperProps nests the selector a level too deep and the paper renders unstyled
// (white on white). Same usage as Fleet's and Team's dialogs.
export function FAConfirmDialog({ open, title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", danger, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, color: "#fff", pb: "6px" }}>{title}</DialogTitle>
      <DialogContent sx={{ pb: "8px" }}>
        <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>{body}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: "24px", pb: "20px", gap: "10px" }}>
        <FAGhostButton onClick={onCancel}>{cancelLabel}</FAGhostButton>
        <FAPrimaryButton danger={danger} onClick={onConfirm}>{confirmLabel}</FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

// Small hook so a page can swap window.confirm for the dialog without hand-rolling the
// open/pending-action state each time: ask(...) resolves true or false.
export function useConfirm() {
  const [request, setRequest] = React.useState(null);
  const resolverRef = React.useRef(null);

  const ask = React.useCallback((options) => {
    setRequest(options);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const settle = (value) => {
    setRequest(null);
    if (resolverRef.current) resolverRef.current(value);
    resolverRef.current = null;
  };

  const dialog = (
    <FAConfirmDialog
      open={request !== null}
      {...(request || {})}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { ask, confirmDialog: dialog };
}

// Dialog footer buttons. The design's .btn.ghost has a surface and border (dev's Cancel
// was bare text) and .btn.primary is a gradient at 9px radius / weight 600 (dev's was
// flat amber at 8px / weight 700). Shared because Fleet, Team and Devices all repeat it.
export function FAGhostButton({ children, onClick, disabled, sx }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        px: "16px", py: "10px", borderRadius: "9px", fontFamily: FONT_FAMILY,
        fontSize: "0.86rem", fontWeight: 600, cursor: disabled ? "default" : "pointer",
        backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
        color: "rgba(255,255,255,0.8)", opacity: disabled ? 0.5 : 1,
        "&:hover": disabled ? undefined : { borderColor: "rgba(255,255,255,0.2)" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function FAPrimaryButton({ children, onClick, disabled, danger, sx }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        px: "16px", py: "10px", borderRadius: "9px", border: "none", fontFamily: FONT_FAMILY,
        fontSize: "0.86rem", fontWeight: 600, whiteSpace: "nowrap",
        cursor: disabled ? "default" : "pointer",
        background: disabled
          ? (danger ? "rgba(239,68,68,0.35)" : "rgba(253,185,64,0.25)")
          : danger ? BAD_RED : `linear-gradient(135deg, ${AMBER}, #E0A020)`,
        color: disabled ? (danger ? "rgba(255,255,255,0.65)" : "rgba(13,17,23,0.6)") : danger ? "#fff" : BG,
        // .btn.primary:hover { filter: brightness(1.05) } — in seven design declarations.
        "&:hover": disabled ? undefined : { filter: "brightness(1.05)" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
